import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

import type {
    AxisConfiguration,
    AxisPreviewItem,
    ShowTopStats,
} from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import { buildAxisPreviewItemsFromRows, extractShowTopStatsFromChartResponse } from 'components/pages/PackArchitecture/packArchitectureChart.helpers';
import { buildAxisPreviewRequest } from 'components/pages/PackArchitecture/packArchitectureChartRequest.helpers';
import { AXIS_PREVIEW_SALES_KPI_IDS } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import { ppaUrls } from 'config/urls';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';

const SHOW_TOP_PREVIEW_DEBOUNCE_MS = 400;

type PreviewFetchSnapshot = Pick<
    AxisConfiguration,
    'sortBy' | 'sortOrder' | 'showTop' | 'groupRestAsOther' | 'showOwnVendorFirst'
>;

type UseAxisConfigurationPreviewParams = {
    open: boolean;
    chartRequest: PricePackArchitectureRequest | null;
    xAxis: string;
    previewConfig: AxisConfiguration;
    /** When true, skips preview fetch (e.g. Show Top input empty or above max). */
    pauseFetch?: boolean;
};

const toFetchSnapshot = (config: AxisConfiguration): PreviewFetchSnapshot => ({
    sortBy: config.sortBy,
    sortOrder: config.sortOrder,
    showTop: config.showTop,
    groupRestAsOther: config.groupRestAsOther,
    showOwnVendorFirst: config.showOwnVendorFirst,
});

const hasNonShowTopChange = (
    previous: PreviewFetchSnapshot | null,
    current: PreviewFetchSnapshot,
) =>
  !previous ||
    previous.sortBy !== current.sortBy ||
    previous.sortOrder !== current.sortOrder ||
    previous.groupRestAsOther !== current.groupRestAsOther ||
    previous.showOwnVendorFirst !== current.showOwnVendorFirst;

export const useAxisConfigurationPreview = ({
    open,
    chartRequest,
    xAxis,
    previewConfig,
    pauseFetch = false,
}: UseAxisConfigurationPreviewParams) => {
    const [previewItems, setPreviewItems] = useState<AxisPreviewItem[]>([]);
    const [showTopStats, setShowTopStats] = useState<ShowTopStats | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const previousSnapshotRef = useRef<PreviewFetchSnapshot | null>(null);

    useEffect(() => {
        if (!open) {
            setPreviewItems([]);
            setShowTopStats(null);
            setIsLoading(false);
            previousSnapshotRef.current = null;
            return undefined;
        }

        if (!chartRequest || previewConfig.showTop <= 0 || pauseFetch) {
            abortRef.current?.abort();
            setIsLoading(false);
            return undefined;
        }

        setIsLoading(true);

        const currentSnapshot = toFetchSnapshot(previewConfig);
        const fetchDelay = hasNonShowTopChange(previousSnapshotRef.current, currentSnapshot)
            ? 0
            : SHOW_TOP_PREVIEW_DEBOUNCE_MS;
        previousSnapshotRef.current = currentSnapshot;

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const configForFetch = previewConfig;

        const fetchPreview = async () => {
            if (abortRef.current !== controller) return;

            try {
                const request = buildAxisPreviewRequest(chartRequest, configForFetch);
                const response = await axios.post(ppaUrls.getPricePackArchitectureUrl, request, {
                    signal: controller.signal,
                });
                const rows = response.data?.data ?? [];
                setPreviewItems(
                    buildAxisPreviewItemsFromRows(rows, xAxis, AXIS_PREVIEW_SALES_KPI_IDS),
                );
                setShowTopStats(extractShowTopStatsFromChartResponse(response.data));
            } catch (err: unknown) {
                const error = err as { name?: string; code?: string };
                if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
                setPreviewItems([]);
                setShowTopStats(null);
            } finally {
                if (abortRef.current === controller) {
                    setIsLoading(false);
                }
            }
        };

        const timer = window.setTimeout(() => {
            void fetchPreview();
        }, fetchDelay);

        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [
        chartRequest,
        open,
        pauseFetch,
        previewConfig,
        xAxis,
    ]);

    return { previewItems, isLoading, showTopStats };
};
